import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  Grid,
} from "@mui/material";
import {
  ShareOutlined as ShareIcon,
  ContentCopy as CopyIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarTodayOutlined as CalendarIcon,
  AccessTimeOutlined as TimeIcon,
  PersonOutline as CreatorIcon,
  ConfirmationNumberOutlined as TicketIcon,
  GroupOutlined as CapacityIcon,
  PercentOutlined as PercentIcon,
  AccountBalanceWalletOutlined as PrizeIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import moment from "moment";
import web3 from "../../../Service/web3";
import {
  lotteryTypeMapper,
  resultStatusTypeMapper,
  LOTTERY_SORT_OPTIONS,
  lotterySortOptions,
  LOTTERY_TYPES,
  STATUS_CONFIG,
} from "../../../../static/Constants";
import { CommonChip, CommonButton } from "../../ui";
import { borderRadiusPx, colors, customShadows } from "../../../../theme";

// Re-export mappers, options & configs
export {
  lotteryTypeMapper,
  resultStatusTypeMapper,
  LOTTERY_SORT_OPTIONS,
  lotterySortOptions,
  LOTTERY_TYPES,
  STATUS_CONFIG,
};

/**
 * Format rollover duration seconds into human readable format
 */
export const formatRolloverDuration = (seconds) => {
  if (!seconds || Number(seconds) <= 0) return "N/A";
  const sec = Number(seconds);
  const days = Math.floor(sec / (60 * 60 * 24));
  const hours = Math.floor((sec % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((sec % (60 * 60)) / 60);

  let formatted = [];
  if (days > 0) formatted.push(`${days}d`);
  if (hours > 0) formatted.push(`${hours}h`);
  if (minutes > 0) formatted.push(`${minutes}m`);
  return formatted.length > 0 ? formatted.join(" ") : `${sec}s`;
};

/**
 * Format Ether safely
 */
export const safeFromWei = (weiAmount) => {
  if (!weiAmount || weiAmount === "0") return "0";
  try {
    return web3.utils.fromWei(String(weiAmount), "ether");
  } catch (e) {
    return String(weiAmount);
  }
};

/**
 * AdminLotteryCard -Card UI for lottery items in the admin dashboard
 */
const AdminLotteryCard = ({
  lottery,
  onViewDetails,
  onShare,
  userRole = "admin",
}) => {
  const [copied, setCopied] = useState(false);

  if (!lottery) return null;

  const typeConfig = LOTTERY_TYPES[lottery.lotteryType] || LOTTERY_TYPES[0];
  const statusInfo = STATUS_CONFIG[lottery.status] || {
    label: `Status ${lottery.status}`,
    variant: "neutral",
    color: colors.slate[600],
    bg: colors.slate[100],
  };

  const TypeIcon = typeConfig.icon;
  const entryFeePol = safeFromWei(lottery.entryFee);
  const totalPrizePol = safeFromWei(lottery.totalPrize);

  const formattedId = lottery.deployedOn
    ? `${moment.unix(lottery.deployedOn).format("DDMMYYYY")}-${lottery.lotteryId}`
    : `ID-${lottery.lotteryId}`;

  const shortCreator = lottery.ownerAddress
    ? `${lottery.ownerAddress.slice(0, 6)}...${lottery.ownerAddress.slice(-4)}`
    : "Platform";

  const handleCopyCreator = (e) => {
    e.stopPropagation();
    if (lottery.ownerAddress) {
      navigator.clipboard.writeText(lottery.ownerAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card
      sx={{
        position: "relative",
        borderRadius: borderRadiusPx.xl,
        bgcolor: colors.background.paper,
        border: `1px solid ${colors.neutral.border}`,
        boxShadow: customShadows.cardSubtle,
        transition: "all 0.25s ease-in-out",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: customShadows.cardHover,
          borderColor: colors.neutral.borderHover,
        },
      }}
    >
      {/* Top Accent Color Bar */}
      <Box
        sx={{
          height: "4px",
          background: typeConfig.gradient,
          width: "100%",
        }}
      />

      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
        {/* Header: Lottery Type Badge, Status Chip & Share Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          {/* Lottery Type Pill */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              bgcolor: typeConfig.bgColor,
              color: typeConfig.color,
              px: 1.5,
              py: 0.6,
              borderRadius: borderRadiusPx.pill,
              fontWeight: 700,
              fontSize: "12px",
            }}
          >
            <TypeIcon sx={{ fontSize: 16 }} />
            <span>{typeConfig.shortName}</span>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {/* Status Chip */}
            <CommonChip
              label={statusInfo.label}
              variant={statusInfo.variant}
              backgroundColor={statusInfo.bg}
              color={statusInfo.color}
              pill
              size="small"
            />

            {/* Share Button */}
            {onShare && (
              <Tooltip title="Copy Shareable Link" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare(lottery);
                  }}
                  sx={{
                    color: colors.slate[400],
                    p: 0.6,
                    borderRadius: borderRadiusPx.md,
                    "&:hover": {
                      color: colors.primary.main,
                      bgcolor: colors.primary.lighter,
                    },
                  }}
                >
                  <ShareIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Lottery ID & Date Deployed */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: colors.text.title,
                fontSize: "17px",
                letterSpacing: "-0.3px",
              }}
            >
              Lottery #{lottery.lotteryId}
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: colors.slate[400],
                fontFamily: "monospace",
                fontWeight: 600,
              }}
            >
              {formattedId}
            </Typography>
          </Box>
        </Box>

        {/* Prize Pool Hero Highlight Box */}
        <Box
          sx={{
            p: 2,
            mb: 2.25,
            borderRadius: borderRadiusPx.lg,
            background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)",
            border: `1px solid ${colors.blue[100]}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: colors.slate[500],
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 0.25,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <PrizeIcon sx={{ fontSize: 14, color: colors.primary.main }} />
              Prize Pool
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 800,
                color: colors.primary.dark,
                lineHeight: 1.2,
              }}
            >
              {totalPrizePol}{" "}
              <Box component="span" sx={{ fontSize: "13px", fontWeight: 700, color: colors.primary.main }}>
                POL
              </Box>
            </Typography>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: colors.slate[500],
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 0.25,
              }}
            >
              Entry Fee
            </Typography>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                color: colors.slate[800],
              }}
            >
              {entryFeePol}{" "}
              <Box component="span" sx={{ fontSize: "11px", color: colors.slate[500] }}>
                POL
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* Metrics Grid */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {/* Slots / Pick Numbers */}
          {lottery.lotteryType !== "3" && (
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 1.2,
                  bgcolor: colors.slate[50],
                  borderRadius: borderRadiusPx.md,
                  border: `1px solid ${colors.neutral.divider}`,
                }}
              >
                <Typography sx={{ fontSize: "11px", color: colors.slate[500], fontWeight: 600, mb: 0.25 }}>
                  Pick Numbers
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.slate[800] }}>
                  {lottery.pickNumbers || "N/A"} slots
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Capacity */}
          {lottery.lotteryType !== "3" && (
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 1.2,
                  bgcolor: colors.slate[50],
                  borderRadius: borderRadiusPx.md,
                  border: `1px solid ${colors.neutral.divider}`,
                }}
              >
                <Typography sx={{ fontSize: "11px", color: colors.slate[500], fontWeight: 600, mb: 0.25 }}>
                  Capacity
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.slate[800] }}>
                  {lottery.capacity || "N/A"} numbers
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Min Players (For MRL) */}
          {lottery.lotteryType === "2" && (
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 1.2,
                  bgcolor: colors.slate[50],
                  borderRadius: borderRadiusPx.md,
                  border: `1px solid ${colors.neutral.divider}`,
                }}
              >
                <Typography sx={{ fontSize: "11px", color: colors.slate[500], fontWeight: 600, mb: 0.25 }}>
                  Min Players
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.slate[800] }}>
                  {lottery.minPlayers || "0"}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Rollover % */}
          {lottery.lotteryType !== "1" && (
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 1.2,
                  bgcolor: colors.slate[50],
                  borderRadius: borderRadiusPx.md,
                  border: `1px solid ${colors.neutral.divider}`,
                }}
              >
                <Typography sx={{ fontSize: "11px", color: colors.slate[500], fontWeight: 600, mb: 0.25 }}>
                  Rollover Share
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.slate[800] }}>
                  {lottery.rolloverperct || 0}%
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Rollover Duration */}
          {(lottery.lotteryType === "2" || lottery.lotteryType === "3") && (
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 1.2,
                  bgcolor: colors.slate[50],
                  borderRadius: borderRadiusPx.md,
                  border: `1px solid ${colors.neutral.divider}`,
                }}
              >
                <Typography sx={{ fontSize: "11px", color: colors.slate[500], fontWeight: 600, mb: 0.25 }}>
                  Rollover Duration
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.slate[800] }}>
                  {formatRolloverDuration(lottery.rolloverdays)}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Partners Share */}
          {lottery.partnershare && (
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 1.2,
                  bgcolor: colors.slate[50],
                  borderRadius: borderRadiusPx.md,
                  border: `1px solid ${colors.neutral.divider}`,
                }}
              >
                <Typography sx={{ fontSize: "11px", color: colors.slate[500], fontWeight: 600, mb: 0.25 }}>
                  Partner Share
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.slate[800] }}>
                  {lottery.partnershare}%
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Timestamps Section */}
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: borderRadiusPx.md,
            bgcolor: colors.slate[50],
            fontSize: "12px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: colors.slate[500] }}>
              <CalendarIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: "12px", color: colors.slate[500] }}>Start Date:</Typography>
            </Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.slate[800] }}>
              {lottery.startTime
                ? moment.unix(lottery.startTime).format("DD/MM/YYYY hh:mm A")
                : "Pending"}
            </Typography>
          </Box>

          {lottery.lotteryType !== 0 && lottery.lotteryType !== "0" && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: colors.slate[500] }}>
                <TimeIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: "12px", color: colors.slate[500] }}>Draw Date:</Typography>
              </Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.slate[800] }}>
                {lottery.drawTime
                  ? moment.unix(lottery.drawTime).format("DD/MM/YYYY hh:mm A")
                  : "Pending"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Creator / Owner Address */}
        {userRole === "admin" && lottery.ownerAddress && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              p: 1,
              bgcolor: "#ffffff",
              borderRadius: borderRadiusPx.sm,
              border: `1px dashed ${colors.neutral.border}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CreatorIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
              <Typography sx={{ fontSize: "12px", color: colors.slate[500], fontWeight: 500 }}>
                Creator:
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontFamily: "monospace",
                  color: colors.slate[700],
                  fontWeight: 600,
                }}
              >
                {shortCreator}
              </Typography>
            </Box>

            <Tooltip title={copied ? "Copied!" : "Copy Creator Address"} arrow>
              <IconButton size="small" onClick={handleCopyCreator} sx={{ p: 0.25 }}>
                {copied ? (
                  <CheckIcon sx={{ fontSize: 15, color: colors.emerald[600] }} />
                ) : (
                  <CopyIcon sx={{ fontSize: 15, color: colors.slate[400] }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Action Button: View Details */}
        <Box sx={{ mt: "auto", pt: 1 }}>
          <CommonButton
            variant="primary"
            fullWidth
            size="small"
            onClick={() => onViewDetails(lottery.lotteryId)}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          >
            View Details
          </CommonButton>
        </Box>
      </Box>
    </Card>
  );
};

AdminLotteryCard.propTypes = {
  lottery: PropTypes.object.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onShare: PropTypes.func,
  userRole: PropTypes.string,
};

export default AdminLotteryCard;
